// Project imports
import { logInfo } from 'lib/util/errors';
import type { LinkField } from '@sitecore-jss/sitecore-jss-react';
// Module-local type imports
import type {
  ResetEcommerceDataLayerObject,
  WindowWithDataLayer,
  CarouselClickDataLayerObject,
  PushableDataLayerObject,
  EcommerceDataLayerObject,
  DataLayerObject,
  LinkClickEventTypes,
  NavMenuDataLayerObject,
} from './_data-layer-types';
import { env } from 'lib/env';
import { processCdpResponse, onEngageLoaded, eventDataBuilder } from 'lib/engage';
import type { BasicTypes } from '@sitecore/engage/types/lib/utils/interfaces';

const PublicUrl = env.PUBLIC_URL;
const DOWNLOAD_FILE_REGEX =
  /\.(pdf|docx?|xlsx?|zip|ashx|wav|mp\d|mov|mpe.*g|avi|mxp|air|zxp|flv|cptx|pptx.*|xml|exe|dmg|jar|bin|msi)/i;

/**
 * Narrowing type predicate used to identify if object is an ecommerce reset data layer push.
 * @param dataLayerObject the value to evaluate
 */
const isDataLayerObject = (
  dataLayerObject: PushableDataLayerObject
): dataLayerObject is DataLayerObject => {
  return 'event' in dataLayerObject;
};

/**
 * Events that should be ignored when sending to Engage SDK
 */
const engageIgnoreEvents = ['pageLoad'];

/**
 * Sends data to Engage SDK if applicable
 * @param dataLayerObject {PushableDataLayerObject} The data layer object to send to Engage SDK
 * @returns {Promise<unknown|null>} The result of the Engage SDK event send, or null if not sent
 */
const sendDataToEngage = (dataLayerObject: PushableDataLayerObject): Promise<unknown | null> => {
  if (!isDataLayerObject(dataLayerObject) || engageIgnoreEvents.includes(dataLayerObject.event)) {
    return Promise.resolve(null);
  }
  let rawEventName =
    dataLayerObject.event ?? (dataLayerObject as { type?: string }).type ?? 'unknownEvent';
  rawEventName = rawEventName.trim().toLocaleUpperCase();
  if (rawEventName.startsWith('E_')) {
    rawEventName = rawEventName.substring(2);
  }
  const eventName = `GTM:${rawEventName}`;
  const skipKeys = ['event', 'type'];
  const thisExt = Object.keys(dataLayerObject).reduce(
    (acc, key) => {
      if (skipKeys.includes(key)) {
        return acc;
      }
      return {
        ...acc,
        [key]: (dataLayerObject as Record<string, BasicTypes>)[key],
      };
    },
    { clickValue: 1 } as { [key: string]: BasicTypes }
  );
  const thisData = eventDataBuilder();
  return onEngageLoaded(async (engageInstance) =>
    engageInstance
      ? processCdpResponse(
          `EngageSDK Event:[${eventName}]`,
          engageInstance.event(eventName, thisData, thisExt)
        )
      : null
  );
};

/**
 * Pushes object name:values onto the Google Tag Manager Datalayer
 * @param dataLayerObject The @see PushableObject data that is pushed to the data layer.
 */
export const gtmDatalayerPush = (dataLayerObject: PushableDataLayerObject) => {
  sendDataToEngage(dataLayerObject).catch((e) => {
    logInfo('GTM: Engage data push failed', e);
  });
  if (typeof window !== 'undefined') {
    const gtmWindow = window as WindowWithDataLayer;
    gtmWindow.dataLayer = gtmWindow.dataLayer || [];
    if (isDataLayerObject(dataLayerObject)) {
      logInfo('GTM: Pushing object to data layer', {
        event: dataLayerObject.event,
        dataLayerObject,
      });
    }
    gtmWindow.dataLayer.push(dataLayerObject);
  }
};

/**
 * Helper function for carousel click data layer pushes
 * @param index Index of the selected item.
 * @param clickAction Sent to GTM as clickAction
 * @param carouselType Type  or title of the carousel
 * @param items Slider items
 */
export const pushCarouselClickDataLayerObject = (
  index: number,
  clickAction: string,
  carouselType: string,
  items: { name: string }[]
) => {
  if (!(items ?? [])[index]) {
    return;
  }
  gtmDatalayerPush({
    event: 'e_carouselClick',
    clickAction: clickAction !== '' ? clickAction : 'carousel - jump dot',
    contentName: items[index].name,
    contentPosition: index + 1,
    carouselType: carouselType,
  } as CarouselClickDataLayerObject);
};

/**
 * Performs an ecommerce-related data layer push by first clearing the stack and then sending data
 * @param dataLayerObject A value deriving from @see ECommerceDataLayerObject containing information to push to the stack.
 */
export const gtmEcommerceDatalayerPush = (dataLayerObject: EcommerceDataLayerObject) => {
  // Remove any exiting ecommerce object from the data layer stack
  gtmDatalayerPush({
    ecommerce: null,
  } as ResetEcommerceDataLayerObject);
  // Then push our incoming value
  gtmDatalayerPush(dataLayerObject);
};

/**
 * Referrer URL check and set
 */
export const referrerUrlSetter = () => {
  let referrerUrl = '/';
  if (typeof window !== 'undefined') {
    if (window.document.referrer !== '') {
      referrerUrl = document.referrer;
    } else {
      referrerUrl = window.sessionStorage.errRef ?? '';
    }
  }
  const publicUrl = PublicUrl;
  if (!referrerUrl.startsWith(publicUrl)) {
    const parsedRefferer = new URL(referrerUrl, PublicUrl);
    referrerUrl = new URL(parsedRefferer.pathname, publicUrl).toString();
  }
  return referrerUrl;
};

/**
 * Determines if a given anchor element is considered an outgoing link
 * @param a The link object to evaluate
 */
export const isExternalLink = (
  a?: HTMLAnchorElement | LinkField | { link: LinkField; openSameWindow: boolean }
) => {
  if (!a) {
    return false;
  }
  const checkHref = (input?: string | null): boolean => {
    if (!input) {
      return false;
    }
    const trimmed = input.trim();
    return trimmed.startsWith('http') && !trimmed.startsWith(PublicUrl ?? '');
  };
  // Check for custom extended case used by LinkA11yWrapper
  if ('openSameWindow' in a) {
    return checkHref(a.link.value?.href) && !a.openSameWindow;
  }
  if ('addEventListener' in a) {
    return (
      checkHref(a.attributes.getNamedItem('href')?.value) ||
      a.attributes.getNamedItem('target')?.value === '_blank'
    );
  }
  return checkHref(a.value?.href);
};
/**
 * Determines if a given anchor element is considered a download link
 * @param a The @see HTMLAnchorElement or @see string value with the link target to evalate
 * @returns true if this link references a downloadable asset
 */
export const isDownloadLink = (a: HTMLAnchorElement | (string | undefined)) => {
  const checkHref = (hRef: string | null | undefined) => {
    if (!hRef) {
      return false;
    }
    return /\/-\/media\//i.test(hRef) || DOWNLOAD_FILE_REGEX.test(hRef);
  };
  if (!a) {
    return false;
  }
  if (typeof a === 'string') {
    return checkHref(a);
  }
  return checkHref(a.getAttribute('href'));
};

/**
 * Determines if a given anchor element is considered an outgoing link
 * @param a The @see HTMLAnchorElment or @see string href value to evaluate
 * @returns true if the link is targeting an email address
 */
export const isEmailLink = (a: HTMLAnchorElement | string | undefined) => {
  if (!a) {
    return false;
  }
  let hRef: string | undefined;
  if (!a || typeof a === 'string') {
    hRef = a;
  } else {
    // If it has attributes it's an anchor element
    hRef = a.attributes.getNamedItem('href')?.value;
  }
  return hRef && hRef.startsWith('mailto:');
};
/**
 * Identifies what event type to use for a given anchor link
 */
export const findEventType = (clickedElement: HTMLAnchorElement): LinkClickEventTypes => {
  // what type of click - inbound?  file?  outbound?  For now just handling inbound/outbound
  if (isExternalLink(clickedElement)) {
    return 'e_outboundClick';
  }
  if (isEmailLink(clickedElement)) {
    return 'e_email';
  }
  if (isDownloadLink(clickedElement)) {
    return 'e_fileDownload';
  }
  return 'e_inPageClick';
};

/**
 * Searches DOM to find the nearest header element
 * @param originalElement the root @see HTMLElement to start searching from
 * @returns A string identifying the category
 */
export const findParentCategory = (originalElement: HTMLElement): string | null => {
  // First, check to see if we were provided a specific category in html
  const dataGtmCategory = (originalElement.getAttribute('data-gtm-category') ?? '').trim();
  if (dataGtmCategory.length > 0) {
    return dataGtmCategory;
  }
  // Grab and store originally clicked element's bounding rect
  const clickedElementBounds = originalElement.getBoundingClientRect();
  // Weird hack to support checking for visibility on browsers that do not have checkVisibility method on htmlelement
  const checkElementForVisibility = (el: HTMLElement) => {
    if ('checkVisibility' in el && typeof el.checkVisibility === 'function') {
      return !!el.checkVisibility();
    }
    return el.offsetHeight > 0 || el.offsetWidth > 0;
  };
  const recursivelySearchForParent = (currentElement: HTMLElement | null): string | null => {
    // If we've ran out of parents then there's no category we can pull.
    if (!currentElement) {
      return null;
    }
    // Check to see if our current element has any header elements.
    const headerElements = (
      [...currentElement.querySelectorAll('h1,h2,h3,h4,h5,h6')] as HTMLElement[]
    ).filter((element: HTMLElement) => {
      if (!checkElementForVisibility(element)) {
        return false;
      }
      // Then make sure that the header element is above our current click target
      const thisElementBounds = element.getBoundingClientRect();
      return thisElementBounds.top < clickedElementBounds.top;
    });
    // If header was found, return it's text.  Otherwise, try again with our parent
    return headerElements.length === 0
      ? recursivelySearchForParent(currentElement.parentElement)
      : headerElements[headerElements.length - 1].innerText;
  };
  // Then let the recursive search function do it's magic
  return recursivelySearchForParent(originalElement.parentElement);
};

/**
 * Finds closest parent with data-component attribute
 */
export const findComponentParent = (currentElement: HTMLElement): HTMLElement => {
  const parent = currentElement.parentElement;
  if (parent) {
    const dataComponentName = parent.getAttribute('data-component');
    if (dataComponentName && !/^helpers\//i.test(dataComponentName)) {
      return parent;
    }
    return findComponentParent(parent);
  }
  return currentElement;
};
/**
 * Gets data-component attribute value of found closest parent
 * separating those two, because for some reason otherwise it doesn't work properly
 */
export const getComponentType = (currentElement: HTMLElement) => {
  const element = findComponentParent(currentElement);
  return element?.getAttribute('data-component');
};

/**
 * Helper function for page navigation click data layer events
 * @param itemName
 * @param menuLocation
 * @returns
 */
export const pageNavigationDataLayerPush = (itemName: string, menuLocation: string) => {
  if (!itemName) {
    return undefined;
  }
  const gmtData: NavMenuDataLayerObject = {
    event: 'e_menuNav',
    menuName: itemName,
    menuType: menuLocation,
  };
  gtmDatalayerPush(gmtData);
  return true;
};

/**
 * Checks for link rel and sets/adds nopener noreferrer
 * @param link
 */
export const setRelNoReffer = (link: HTMLAnchorElement) => {
  const getRel = link.getAttribute('rel')?.split(' ');
  const noRef = ['noopener', 'noreferrer'];
  const newRel = (getRel ? [...new Set([...getRel, ...noRef])] : noRef).join(' ');
  link.setAttribute('rel', newRel);
};
